/**
 * Frame-driven tween primitive (ADR-0014 §3 / HEX-46 §C).
 *
 * The renderer already runs a `frame(deltaMs)` loop driven off
 * `requestAnimationFrame`; this registry plugs into the same tick so every
 * tween advances against the same clock as the frontier pulse. No per-tween
 * rAF — one handler per frame, regardless of how many tweens are live.
 *
 * The tween is **library-free** (ADR-0014 locks no animation deps) and
 * **unit-testable** without `performance.now()` — tests advance the registry
 * with a mock `deltaMs`.
 *
 * Usage:
 * ```
 * const tweens = new TweenRegistry();
 * tweens.start({ durationMs: 180, easing: easeOutQuad, onUpdate: (t) => ... });
 * // inside the renderer's frame loop:
 * tweens.advance(deltaMs);
 * ```
 */

/** Pure easing — maps `[0, 1] → [0, 1]` with f(0)=0, f(1)=1 (relaxed for back). */
export type Easing = (t: number) => number;

export const easeLinear: Easing = (t) => t;

export const easeOutQuad: Easing = (t) => 1 - (1 - t) * (1 - t);

export const easeInOutSine: Easing = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

/**
 * Mild overshoot back-out (ADR-0014 §3 — `s = 1.4`). Returns slightly above 1
 * before settling; consumers that need a strict [0, 1] band must clamp.
 */
export const easeOutBack: Easing = makeEaseOutBack(1.4);

export function makeEaseOutBack(s: number): Easing {
  return (t) => {
    const m = t - 1;
    return 1 + m * m * ((s + 1) * m + s);
  };
}

export interface TweenOptions {
  readonly durationMs: number;
  readonly easing: Easing;
  /** Called every frame with eased `[0, 1]` progress (may exceed 1 with back). */
  readonly onUpdate: (eased: number, rawT: number) => void;
  /** Optional completion callback fired exactly once when raw `t` reaches 1. */
  readonly onComplete?: () => void;
}

export interface TweenHandle {
  cancel(): void;
  isActive(): boolean;
}

interface Tween {
  durationMs: number;
  easing: Easing;
  onUpdate: (eased: number, rawT: number) => void;
  onComplete?: () => void;
  elapsedMs: number;
  done: boolean;
}

/**
 * Active set of running tweens. `advance(deltaMs)` drives every live tween
 * forward by the same `deltaMs`; completed tweens are pulled out of the set
 * before their `onComplete` fires (so a completion handler that starts a new
 * tween doesn't trip a re-entry).
 */
export class TweenRegistry {
  private readonly tweens = new Set<Tween>();

  start(opts: TweenOptions): TweenHandle {
    const tween: Tween = {
      durationMs: Math.max(0, opts.durationMs),
      easing: opts.easing,
      onUpdate: opts.onUpdate,
      ...(opts.onComplete ? { onComplete: opts.onComplete } : {}),
      elapsedMs: 0,
      done: false,
    };
    // Zero-duration tween completes synchronously — fire t=1 once then exit
    // so callers can use durationMs:0 as an "apply final value" shortcut.
    if (tween.durationMs === 0) {
      tween.onUpdate(opts.easing(1), 1);
      tween.onComplete?.();
      tween.done = true;
      return { cancel: () => {}, isActive: () => false };
    }
    this.tweens.add(tween);
    return {
      cancel: () => {
        if (tween.done) return;
        tween.done = true;
        this.tweens.delete(tween);
      },
      isActive: () => !tween.done,
    };
  }

  advance(deltaMs: number): void {
    if (this.tweens.size === 0 || deltaMs <= 0) return;
    // Snapshot — completion callbacks may start new tweens, which we must
    // skip until the next frame to avoid double-ticking them.
    const snapshot = Array.from(this.tweens);
    for (const tween of snapshot) {
      if (tween.done) continue;
      tween.elapsedMs += deltaMs;
      const rawT = Math.min(1, tween.elapsedMs / tween.durationMs);
      tween.onUpdate(tween.easing(rawT), rawT);
      if (rawT >= 1) {
        tween.done = true;
        this.tweens.delete(tween);
        tween.onComplete?.();
      }
    }
  }

  cancelAll(): void {
    for (const tween of this.tweens) tween.done = true;
    this.tweens.clear();
  }

  /** Test seam — number of live tweens. */
  size(): number {
    return this.tweens.size;
  }
}
